<template>
  <nav
    class="navbar font-sans antialiased"
    :class="{
      'navbar-scrolled': isScrolled,
      'with-sidebar': isAuthenticated && !showPublicNav,
      'sidebar-collapsed': sidebarCollapsed && !showPublicNav,
      'public-navbar': showPublicNav || !isAuthenticated,
      'public-navbar--tenant': isTenantPublicNavbar,
      'navbar-layout-stack':
        isAuthenticated && !showPublicNav && sidebarLayout === 'stack',
      'navbar-layout-cards':
        isAuthenticated && !showPublicNav && isCardsNav,
    }"
  >
    <div class="navbar-container" :class="{ 'container-with-sidebar': isAuthenticated && !showPublicNav }">
      <!-- Authenticated app shell: optional compact logo + sidebar toggle (not on marketing home) -->
      <div v-if="isAuthenticated && !showPublicNav" class="navbar-dash-leading">
        <router-link
          v-if="dashShowCompactLogo"
          to="/"
          class="navbar-brand navbar-brand--dash-logo"
        >
          <BrandLogoMark
            :name="brandAppName"
            :logo-url="brandLogoUrl"
            :primary-color="brandPrimaryColor"
            :tenant-host="isWorkspaceTenant"
            size="sm"
            layout="horizontal"
            :show-name="isCardsNav || !sidebarCollapsed"
          />
        </router-link>
        <button
          v-if="isCardsNav"
          type="button"
          class="sidebar-toggle nav-apps-toggle"
          :class="{ 'is-open': navCardsOpen }"
          @click="toggleNavCards"
          :title="navCardsOpen ? 'Close apps' : 'Open apps'"
          :aria-label="navCardsOpen ? 'Close apps' : 'Open apps'"
        >
          <i :class="navCardsOpen ? 'fas fa-times' : 'fas fa-th-large'"></i>
        </button>
        <button
          v-else-if="dashShowNavToggle"
          type="button"
          class="sidebar-toggle"
          :class="{ 'mobile-toggle': isMobile }"
          @click="toggleSidebar"
          :title="sidebarCollapsed ? 'Open Sidebar' : 'Collapse Sidebar'"
        >
          <i class="fas fa-bars"></i>
        </button>
      </div>

      <!-- Marketing / guest: hamburger + full logo -->
      <template v-else>
        <router-link v-if="showPublicNav || !isAuthenticated" to="/" class="navbar-brand">
          <BrandLogoMark
            :name="brandAppName"
            :logo-url="brandLogoUrl"
            :primary-color="brandPrimaryColor"
            :tenant-host="isWorkspaceTenant"
            :size="isTenantPublicNavbar ? 'sm' : 'md'"
            layout="horizontal"
          />
        </router-link>
        <button
          v-if="showClinchMarketingLinks && publicNavCompact"
          type="button"
          class="mobile-menu-toggle"
          @click="toggleMobileMenu"
          :class="{ active: showMobileMenu }"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </template>

      <!-- Desktop public nav -->
      <div
        v-if="showClinchMarketingLinks && !publicNavCompact"
        class="navbar-menu"
      >
        <PublicNavLinks :whatsapp-entry-url="whatsappEntryUrl" @navigate="closeMobileMenu" />
      </div>

      <!-- Mobile menu — teleported so fixed positioning is vs. viewport -->
      <Teleport to="body" :disabled="!publicNavCompact">
        <ClAnimatePresence>
          <ClMotion
            v-if="showClinchMarketingLinks && showMobileMenu && publicNavCompact"
            key="navbar-mobile-menu"
            preset="slideInRight"
            as="div"
            class="navbar-menu show"
          >
            <PublicNavLinks
              :whatsapp-entry-url="whatsappEntryUrl"
              @navigate="closeMobileMenu"
            />
          </ClMotion>
        </ClAnimatePresence>
      </Teleport>

      <!-- User Menu (Authenticated) - orders, tickets, notifications; profile on Icon top -->
      <div class="navbar-user-menu navbar-dash-actions" v-if="isAuthenticated && !showPublicNav">
        <HelpLauncher v-if="currentUser" />
        <div
          v-if="showSetupNav"
          class="setup-nav"
          ref="setupNav"
        >
          <button
            type="button"
            class="icon-button setup-workspace-button"
            :class="{
              'has-notifications': setupMissingCount > 0,
              'setup-workspace-button--alert': setupMissingCount > 0,
              'is-open': setupMenuOpen,
            }"
            :title="setupNavTitle"
            :aria-expanded="setupMenuOpen ? 'true' : 'false'"
            aria-haspopup="true"
            aria-controls="setup-readiness-menu"
            @click.stop="toggleSetupMenu"
          >
            <i class="fas fa-clipboard-check" aria-hidden="true"></i>
            <span class="notification-badge notification-badge--setup" v-if="setupMissingCount > 0">
              {{ formatBadgeCount(setupMissingCount) }}
            </span>
          </button>
          <div
            v-if="setupMenuOpen"
            id="setup-readiness-menu"
            class="setup-dropdown"
            role="menu"
            @click.stop
          >
            <div class="setup-dropdown__head">
              <strong>{{ setupMenuTitle }}</strong>
              <span v-if="setupMissingCount > 0" class="setup-dropdown__count">
                {{ setupMissingCount }} to finish
              </span>
              <span v-else class="setup-dropdown__count setup-dropdown__count--ok">All set</span>
            </div>
            <ul v-if="setupMissingItems.length" class="setup-dropdown__list">
              <li v-for="item in setupMissingItems" :key="item.key" role="none">
                <router-link
                  :to="setupItemTo(item.path)"
                  class="setup-dropdown__item"
                  role="menuitem"
                  @click="closeSetupMenu"
                >
                  <span class="setup-dropdown__item-label">
                    {{ item.label }}
                    <span v-if="item.critical" class="setup-dropdown__pill">Required</span>
                  </span>
                  <span class="setup-dropdown__item-detail">{{ item.detail }}</span>
                  <i class="fas fa-chevron-right setup-dropdown__chevron" aria-hidden="true"></i>
                </router-link>
              </li>
            </ul>
            <p v-else class="setup-dropdown__empty">
              {{ isWorkspaceTenant ? 'My Platform setup looks complete.' : 'Platform setup looks complete.' }}
            </p>
            <router-link
              v-if="isWorkspaceTenant"
              to="/onboarding"
              class="setup-dropdown__footer"
              @click="closeSetupMenu"
            >
              Open setup guide
            </router-link>
          </div>
        </div>
        <a
          v-if="canCreditWalletNav"
          href="/manage/subscription?tab=wallet"
          class="icon-button credit-wallet-nav-button"
          :class="{
            'credit-wallet-nav-button--balance': creditWalletBalanceLabel,
            'credit-wallet-nav-button--empty': creditWalletIsEmpty,
          }"
          :title="creditWalletTitle"
          :aria-label="creditWalletTitle"
          @click.prevent="onCreditWalletNavClick"
        >
          <i class="fas fa-wallet" aria-hidden="true"></i>
          <span
            v-if="creditWalletBalanceLabel || creditWalletMeters.length"
            class="credit-wallet-nav-amount"
            :class="{ 'credit-wallet-nav-amount--empty': creditWalletIsEmpty }"
          >
            <i
              v-if="creditWalletIsEmpty"
              class="fas fa-triangle-exclamation credit-wallet-nav-warn"
              aria-hidden="true"
            ></i>
            <span v-if="creditWalletBalanceLabel">{{ creditWalletBalanceLabel }}</span>
            <span
              v-for="meter in creditWalletMeters"
              :key="meter.key"
              class="credit-wallet-nav-meter"
              :class="{ 'is-charging': meter.charging }"
            >
              {{ meter.short }}
            </span>
          </span>
        </a>
        <router-link
          v-if="canOrdersNav"
          to="/dashboard/orders"
          class="icon-button orders-nav-button"
          :class="{ 'has-notifications': orderCount > 0 }"
          title="Orders (paid or processing)"
        >
          <i class="fas fa-shopping-cart"></i>
          <span class="notification-badge" v-if="orderCount > 0">{{ formatBadgeCount(orderCount) }}</span>
        </router-link>
        <router-link
          v-if="canOrderTicketsNav"
          to="/manage/tickets?tab=order"
          class="icon-button tickets-nav-button"
          :class="{ 'has-notifications': orderTicketCount > 0 }"
          title="Order tickets"
        >
          <i class="fas fa-ticket-alt"></i>
          <span class="notification-badge" v-if="orderTicketCount > 0">{{ formatBadgeCount(orderTicketCount) }}</span>
        </router-link>
        <router-link
          v-if="canSupportTicketsNav"
          to="/manage/tickets?tab=support"
          class="icon-button support-tickets-button"
          :class="{ 'has-notifications': supportTicketCount > 0 }"
          title="Support tickets"
        >
          <i class="fas fa-headset"></i>
          <span class="notification-badge" v-if="supportTicketCount > 0">{{ formatBadgeCount(supportTicketCount) }}</span>
        </router-link>
        <button
          class="icon-button notification-button"
          @click="toggleNotifications"
          :class="{ 'has-notifications': notificationCount > 0 }"
        >
          <i class="fas fa-bell"></i>
          <span class="notification-badge" v-if="notificationCount > 0">{{ formatBadgeCount(notificationCount) }}</span>
        </button>
        <div
          v-if="showProfileInNavbar && currentUser"
          class="navbar-sidebar-profile"
          :class="`navbar-sidebar-profile--${sidebarProfileStyle}`"
          :title="`${getUserDisplayName} · ${getUserRole}`"
        >
          <div class="navbar-sidebar-profile__avatar">
            <i class="fas fa-user" aria-hidden="true"></i>
          </div>
          <div class="navbar-sidebar-profile__info">
            <div class="navbar-sidebar-profile__name">{{ getUserDisplayName }}</div>
            <div class="navbar-sidebar-profile__role">{{ getUserRole }}</div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Swal from 'sweetalert2'
import eventBus from '../utils/eventBus'
import api from '@/utils/api'
import ClAnimatePresence from './ds/ClAnimatePresence.vue'
import ClMotion from './ds/ClMotion.vue'
import PublicNavLinks from './PublicNavLinks.vue'
import { isPublicMarketingRoute } from '@/utils/publicMarketingRoutes'
import { isTenantWorkspaceHost } from '@/utils/saasPlatform'
import { canAccessResource, canOpenWorkspaceFeature, isPlatformOperator } from '@/composables/usePermissions'
import {
  DEFAULT_CLINCH_THEME,
  isCardsNavLayout,
  loadBrandThemeFromStorage,
  resolveProfilePlacement,
} from '@/theme/brandTheme'
import BrandLogoMark from '@/components/brand/BrandLogoMark.vue'
import HelpLauncher from '@/components/help/HelpLauncher.vue'
import { WORKSPACE_SETUP_STEPS } from '@/theme/workspaceSetupGuide'
import { isSetupPromptSnoozed, snoozeSetupPrompt } from '@/utils/setupPromptSnooze'
import {
  applyCreditWalletPayload,
  CREDIT_WALLET_PATH,
  CREDIT_WALLET_QUERY,
  isCreditWalletLocked,
  creditWalletAllowances,
  shouldAutoShowCreditWalletEmptyAlert,
  showCreditWalletEmptyAlert,
  clearCreditWalletEmptyAlertFlag,
} from '@/utils/creditWalletAi'

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const SETUP_LIVE_LABELS = Object.fromEntries(
  WORKSPACE_SETUP_STEPS.filter((step) => step.liveCheck).map((step) => [
    step.liveCheck,
    step.title,
  ]),
)

export default {
  name: 'Navigation',
  components: { ClAnimatePresence, ClMotion, PublicNavLinks, BrandLogoMark, HelpLauncher },
  data() {
    const brand = loadBrandThemeFromStorage()
    return {
      showMobileMenu: false,
      notificationCount: 0,
      orderCount: 0,
      orderTicketCount: 0,
      supportTicketCount: 0,
      setupMissingCount: 0,
      setupMissingLabels: [],
      setupMissingItems: [],
      setupPromptOpen: false,
      creditWalletBalance: null,
      creditWalletVisible: false,
      walletAllowances: null,
      setupMenuOpen: false,
      setupScope: null,
      sidebarCollapsed: false,
      isMobile: false,
      publicNavCompact: false,
      isScrolled: false,
      brandLogoUrl: brand.logoUrl || DEFAULT_CLINCH_THEME.logoUrl,
      brandAppName: brand.appName || DEFAULT_CLINCH_THEME.appName,
      brandPrimaryColor: brand.colors?.primary || DEFAULT_CLINCH_THEME.colors.primary,
      sidebarLayout: brand.sidebar?.layout || DEFAULT_CLINCH_THEME.sidebar.layout,
      sidebarProfileStyle:
        brand.sidebar?.profileStyle || DEFAULT_CLINCH_THEME.sidebar.profileStyle || 'panel',
      sidebarProfilePosition:
        brand.sidebar?.profilePosition || DEFAULT_CLINCH_THEME.sidebar.profilePosition || 'auto',
      navCardsOpen: false,
    }
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'currentUser', 'whatsappEntryUrl']),

    showProfileInNavbar() {
      return resolveProfilePlacement({
        layout: this.sidebarLayout,
        profilePosition: this.sidebarProfilePosition,
      }).showInNavbar
    },
    isCardsNav() {
      return isCardsNavLayout(this.sidebarLayout)
    },
    isWorkspaceTenant() {
      return isTenantWorkspaceHost()
    },
    isTenantPublicNavbar() {
      return this.isWorkspaceTenant && (this.showPublicNav || !this.isAuthenticated)
    },
    showPublicNav() {
      return isPublicMarketingRoute(this.$route.path)
    },
    showClinchMarketingLinks() {
      if (this.isWorkspaceTenant) return false
      if (this.isAuthenticated && !this.showPublicNav) return false
      return this.showPublicNav || !this.isAuthenticated
    },
    isAdmin() {
      if (!this.isAuthenticated || !this.currentUser) return false
      return isPlatformOperator(this.currentUser)
    },
    canOpenCreditWallet() {
      return canOpenWorkspaceFeature(this.currentUser, 'billing')
    },
    canCreditWalletNav() {
      if (!this.isAuthenticated || this.showPublicNav) return false
      return this.creditWalletVisible || this.isWorkspaceTenant
    },
    creditWalletBalanceLabel() {
      if (this.creditWalletBalance === null) return ''
      const n = Number(this.creditWalletBalance)
      if (!Number.isFinite(n)) return ''
      return `R${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    creditWalletIsEmpty() {
      return isCreditWalletLocked()
    },
    creditWalletMeters() {
      if (!this.canCreditWalletNav) return []
      // Component data, so Vue refreshes the chip after the nav response.
      // Tests call this without that field and still read the shared snapshot.
      const allowances = Object.prototype.hasOwnProperty.call(this, 'walletAllowances')
        ? this.walletAllowances
        : creditWalletAllowances()
      if (!allowances) return []
      const ai = allowances.ai || {}
      const aiIncluded = Number(ai.included) || 0
      const aiUsed = Number(ai.used) || 0
      let aiRemaining = Number.isFinite(Number(ai.remaining))
        ? Number(ai.remaining)
        : Math.max(0, aiIncluded - aiUsed)
      if (aiIncluded > 0 && aiRemaining <= 0 && aiUsed < aiIncluded) {
        aiRemaining = aiIncluded - aiUsed
      }
      const aiCharging = aiIncluded > 0 && (ai.charging === true || aiRemaining <= 0)
      const service = (allowances.free_conversations || []).find((row) => row.key === 'service') || {}
      const serviceIncluded = Number(service.included) || 0
      const serviceUsed = Number(service.used) || 0
      let serviceRemaining = Number.isFinite(Number(service.remaining))
        ? Number(service.remaining)
        : Math.max(0, serviceIncluded - serviceUsed)
      if (serviceIncluded > 0 && serviceRemaining <= 0 && serviceUsed < serviceIncluded) {
        serviceRemaining = serviceIncluded - serviceUsed
      }
      const serviceCharging = serviceIncluded > 0 && serviceRemaining <= 0
      const meters = []
      if (aiIncluded > 0) {
        meters.push({
          key: 'ai',
          charging: aiCharging,
          short: aiCharging
            ? 'AI charging'
            : `AI ${aiRemaining.toLocaleString('en-ZA')} left`,
        })
      }
      if (serviceIncluded > 0) {
        meters.push({
          key: 'service',
          charging: serviceCharging,
          short: serviceCharging
            ? 'Service over free'
            : `${serviceRemaining.toLocaleString('en-ZA')} service free`,
        })
      }
      return meters
    },
    creditWalletMeterSummary() {
      return this.creditWalletMeters.map((row) => row.short).join(' · ')
    },
    creditWalletTitle() {
      const extra = this.creditWalletMeterSummary
      if (this.creditWalletIsEmpty && this.creditWalletBalanceLabel) {
        const paused = `Credit wallet is ${this.creditWalletBalanceLabel} — WhatsApp conversations are paused. Click to top up.`
        return extra ? `${paused} ${extra}.` : paused
      }
      if (this.creditWalletBalanceLabel) {
        return extra
          ? `Credit wallet: ${this.creditWalletBalanceLabel} · ${extra}`
          : `Credit wallet: ${this.creditWalletBalanceLabel}`
      }
      return extra ? `Credit wallet · ${extra}` : 'Credit wallet'
    },
    canOrdersNav() {
      const perms = this.currentUser?.permissions || []
      return canAccessResource(perms, 'operations', 'orders')
    },
    canOrderTicketsNav() {
      const perms = this.currentUser?.permissions || []
      return canAccessResource(perms, 'tickets', 'issue_tickets')
    },
    canSupportTicketsNav() {
      const perms = this.currentUser?.permissions || []
      return canAccessResource(perms, 'tickets', 'support_tickets')
    },
    showSetupNav() {
      if (!this.isAuthenticated || this.showPublicNav) return false
      if (this.isWorkspaceTenant) return true
      return this.isAdmin
    },
    setupMenuTitle() {
      return this.isWorkspaceTenant ? 'My Platform setup' : 'Platform setup'
    },
    setupNavTitle() {
      if (this.setupMissingCount > 0) {
        const names = this.setupMissingLabels.join(', ')
        return `${this.setupMenuTitle} — missing: ${names}`
      }
      return this.setupMenuTitle
    },
    getUserDisplayName() {
      if (!this.currentUser) return 'User'
      return (
        this.currentUser.name ||
        this.currentUser.email?.split('@')[0] ||
        'User'
      )
    },
    getUserRole() {
      if (!this.currentUser) return 'User'
      return (
        this.currentUser.user_type?.name ||
        this.currentUser.user_type?.code ||
        'User'
      )
    },
    /** Logo in dashboard bar when sidebar is collapsed (desktop), cards nav, or on small screens */
    dashShowCompactLogo() {
      if (!this.isAuthenticated || this.showPublicNav) return false
      if (this.isCardsNav) return true
      return (this.sidebarCollapsed && !this.isMobile) || this.isMobile
    },
    /** When the top bar should show the sidebar toggle (desktop rail hides duplicate control when collapsed) */
    dashShowNavToggle() {
      if (!this.isAuthenticated || this.showPublicNav) return false
      if (this.isMobile) return this.sidebarCollapsed
      return !this.sidebarCollapsed
    }
  },
  watch: {
    '$route.path'() {
      this.closeSetupMenu()
      if (this.isAuthenticated && !this.showPublicNav) this.fetchNavbarCounts()
    }
  },
  methods: {
    ...mapActions(['logout']),
    toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu
      if (this.showMobileMenu) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    },
    closeMobileMenu() {
      this.showMobileMenu = false
      document.body.style.overflow = ''
    },
    formatBadgeCount(count) {
      const n = Number(count) || 0
      return n > 99 ? '99+' : String(n)
    },
    async fetchNavbarCounts() {
      if (!this.isAuthenticated || this.showPublicNav) return
      try {
        const res = await api.get('/notifications/summary/', {
          headers: { 'X-Session-Key': localStorage.getItem('session_key') },
        })
        const data = res.data || {}
        const notifications = data.notifications ?? (
          (data.invites ?? 0) + (data.products ?? 0) + (data.business ?? 0)
        )
        this.notificationCount = notifications
        this.orderCount = data.orders ?? 0
        this.orderTicketCount = data.issue_tickets ?? 0
        this.supportTicketCount = data.support_tickets ?? 0
      } catch {
        this.notificationCount = 0
        this.orderCount = 0
        this.orderTicketCount = 0
        this.supportTicketCount = 0
      }
      this.fetchCreditWalletBalance()
      await this.fetchSetupReadiness()
    },
    async fetchCreditWalletBalance() {
      if (!this.isAuthenticated || this.showPublicNav) {
        this.creditWalletBalance = null
        this.creditWalletVisible = false
        return
      }
      try {
        const { data } = await api.get('/wallets/corporate/nav/')
        this.creditWalletVisible = true
        this.creditWalletBalance = data?.balance ?? null
        applyCreditWalletPayload(data || {})
        this.syncWalletAllowances?.()
        if (this.canOpenCreditWallet && shouldAutoShowCreditWalletEmptyAlert()) {
          const go = await showCreditWalletEmptyAlert()
          if (go) {
            this.$router.push({ path: CREDIT_WALLET_PATH, query: CREDIT_WALLET_QUERY })
          }
        }
      } catch (err) {
        if (err.response?.status === 404) {
          this.creditWalletVisible = false
          this.creditWalletBalance = null
          return
        }
        if (!this.isWorkspaceTenant) {
          this.creditWalletBalance = null
        }
      }
    },
    async onCreditWalletNavClick() {
      if (!this.canOpenCreditWallet) return
      if (this.creditWalletIsEmpty) {
        const go = await showCreditWalletEmptyAlert()
        if (!go) return
      }
      this.$router.push({ path: CREDIT_WALLET_PATH, query: CREDIT_WALLET_QUERY })
    },
    onCreditWalletChanged(balance) {
      if (balance === undefined || balance === null) {
        this.fetchCreditWalletBalance()
        return
      }
      this.creditWalletBalance = balance
      const n = Number(balance)
      if (Number.isFinite(n) && n > 0) {
        applyCreditWalletPayload({ balance, billed_ai_blocked: false })
        this.syncWalletAllowances?.()
        clearCreditWalletEmptyAlertFlag()
        return
      }
      if (typeof this.fetchCreditWalletBalance === 'function') {
        this.fetchCreditWalletBalance()
      }
    },
    syncWalletAllowances() {
      this.walletAllowances = creditWalletAllowances()
    },
    setupItemTo(path) {
      const raw = String(path || '').trim() || '/'
      if (raw.includes('?')) {
        const [pathname, qs] = raw.split('?')
        const query = {}
        new URLSearchParams(qs).forEach((value, key) => {
          query[key] = value
        })
        return { path: pathname, query }
      }
      return raw
    },
    toggleSetupMenu() {
      this.setupMenuOpen = !this.setupMenuOpen
      if (this.setupMenuOpen) {
        this.fetchSetupReadiness()
      }
    },
    closeSetupMenu() {
      this.setupMenuOpen = false
    },
    onDocumentClickSetup(event) {
      if (!this.setupMenuOpen) return
      const root = this.$refs.setupNav
      if (root && !root.contains(event.target)) {
        this.closeSetupMenu()
      }
    },
    async fetchSetupReadiness() {
      if (!this.showSetupNav) {
        this.setupMissingCount = 0
        this.setupMissingLabels = []
        this.setupMissingItems = []
        this.setupScope = null
        return
      }
      try {
        const { data } = await api.get('/setup-readiness/')
        const missing = Array.isArray(data?.missing) ? data.missing : []
        this.setupScope = data?.scope || null
        this.setupMissingItems = missing.map((item) => {
          const key = item?.key || ''
          const guideLabel = SETUP_LIVE_LABELS[key]
          if (!guideLabel) return item
          return { ...item, label: guideLabel }
        })
        this.setupMissingCount = Number(data?.missing_count) || this.setupMissingItems.length
        this.setupMissingLabels = this.setupMissingItems
          .map((m) => m.label || m.key)
          .filter(Boolean)
        this.promptWorkspaceSetup()
      } catch {
        this.setupMissingCount = 0
        this.setupMissingLabels = []
        this.setupMissingItems = []
        this.setupScope = null
      }
    },
    async fetchWorkspaceSetupReadiness() {
      return this.fetchSetupReadiness()
    },
    /** True while the user is on a page that fixes one of the missing setup items. */
    onSetupPage() {
      const current = this.$route?.path || ''
      const paths = ['/onboarding', ...this.setupMissingItems.map((item) => String(item.path || '').split('?')[0])]
      return paths.some((p) => p && p !== '/' && (current === p || current.startsWith(`${p}/`)))
    },
    /**
     * Workspace admins must finish setup: required gaps pop up on every page until fixed;
     * optional-only gaps pop up once per browser session. "Remind me later" snoozes either
     * for SETUP_PROMPT_SNOOZE_MS or until the next login.
     */
    async promptWorkspaceSetup() {
      if (!this.isWorkspaceTenant || !canOpenWorkspaceFeature(this.currentUser, 'onboarding')) return
      const missing = this.setupMissingItems
      if (!missing.length || this.setupPromptOpen || Swal.isVisible() || this.onSetupPage()) return
      const userId = this.currentUser?.id
      if (isSetupPromptSnoozed(userId)) return
      const required = missing.filter((item) => item.critical)
      const sessionKey = `setup-prompt-seen:${userId || 'user'}`
      if (!required.length && sessionStorage.getItem(sessionKey)) return

      const rows = (required.length ? required : missing)
        .map((item) => `<li><strong>${escapeHtml(item.label || item.key)}</strong>`
          + (item.detail ? `<br><small>${escapeHtml(item.detail)}</small>` : '') + '</li>')
        .join('')
      const target = (required[0] || missing[0]).path || '/onboarding'

      this.setupPromptOpen = true
      try {
        const result = await Swal.fire({
          icon: required.length ? 'warning' : 'info',
          title: required.length ? 'Finish setting up My Platform' : 'My Platform setup is almost done',
          html: `<p>${required.length
            ? 'These steps are required before My Platform can trade:'
            : 'A few optional steps are still open:'}</p><ul class="text-start">${rows}</ul>`,
          confirmButtonText: 'Set up now',
          confirmButtonColor: '#0066cc',
          showDenyButton: true,
          denyButtonText: 'Remind me later',
          denyButtonColor: '#6c757d',
          showCancelButton: false,
          allowOutsideClick: !required.length,
          allowEscapeKey: !required.length,
        })
        if (!required.length) sessionStorage.setItem(sessionKey, '1')
        if (result.isDenied) snoozeSetupPrompt(userId)
        if (result.isConfirmed) this.$router.push(this.setupItemTo(target))
      } finally {
        this.setupPromptOpen = false
      }
    },
    toggleNotifications() {
      const query = {}
      if (this.$route.query.tab) {
        query.tab = this.$route.query.tab
      }
      this.$router.push({ path: '/manage/notifications', query })
    },
    toggleSidebar() {
      eventBus.emit('toggle-sidebar')
    },
    toggleNavCards() {
      eventBus.emit('toggle-nav-cards')
    },
    handleNavCardsOpenChanged(open) {
      this.navCardsOpen = Boolean(open)
    },
    handleSidebarStateChanged(collapsed) {
      this.sidebarCollapsed = collapsed
    },
    checkMobile() {
      const w = window.innerWidth
      this.isMobile = w <= 768
      this.publicNavCompact = w <= 1024
    },
    handleScroll() {
      this.isScrolled = window.scrollY > 50
    },
    onBrandThemeChanged(event) {
      const theme = event?.detail?.theme || loadBrandThemeFromStorage()
      this.brandLogoUrl = theme.logoUrl || DEFAULT_CLINCH_THEME.logoUrl
      this.brandAppName = theme.appName || DEFAULT_CLINCH_THEME.appName
      this.brandPrimaryColor = theme.colors?.primary || DEFAULT_CLINCH_THEME.colors.primary
      this.sidebarLayout =
        theme.sidebar?.layout || DEFAULT_CLINCH_THEME.sidebar.layout
      this.sidebarProfileStyle =
        theme.sidebar?.profileStyle || DEFAULT_CLINCH_THEME.sidebar.profileStyle || 'panel'
      this.sidebarProfilePosition =
        theme.sidebar?.profilePosition || DEFAULT_CLINCH_THEME.sidebar.profilePosition || 'auto'
      if (!isCardsNavLayout(this.sidebarLayout)) {
        this.navCardsOpen = false
      }
    }
  },
  created() {
    // Attach before auth fetch — bootstrapBrandThemeAsync often finishes while
    // mounted() is awaiting fetchCurrentUser, which left the public navbar on Clinch.
    this.onBrandThemeChanged()
    this._onCreditWalletAllowances = () => {
      this.walletAllowances = creditWalletAllowances()
    }
    eventBus.on('credit-wallet-ai-changed', this._onCreditWalletAllowances)
    if (typeof window !== 'undefined') {
      window.addEventListener('brand-theme-changed', this.onBrandThemeChanged)
    }
  },
  async mounted() {
    this.onBrandThemeChanged()
    if (!this.isAuthenticated) {
      try {
        await this.$store.dispatch('fetchCurrentUser')
      } catch (error) {
        // User not authenticated
      }
    }
    this.fetchNavbarCounts()

    eventBus.on('sidebar-state-changed', this.handleSidebarStateChanged)
    eventBus.on('nav-cards-open-changed', this.handleNavCardsOpenChanged)
    eventBus.on('order-status-updated', this.fetchNavbarCounts)
    eventBus.on('workspace-setup-changed', this.fetchWorkspaceSetupReadiness)
    eventBus.on('credit-wallet-changed', this.onCreditWalletChanged)
    window.addEventListener('notifications-updated', this.fetchNavbarCounts)
    
    const savedState = localStorage.getItem('sidebarCollapsed')
    if (savedState !== null) {
      this.sidebarCollapsed = savedState === 'true'
    }

    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    window.addEventListener('scroll', this.handleScroll)
    document.addEventListener('click', this.onDocumentClickSetup)
  },
  beforeUnmount() {
    eventBus.off('sidebar-state-changed', this.handleSidebarStateChanged)
    eventBus.off('nav-cards-open-changed', this.handleNavCardsOpenChanged)
    eventBus.off('order-status-updated', this.fetchNavbarCounts)
    eventBus.off('workspace-setup-changed', this.fetchWorkspaceSetupReadiness)
    eventBus.off('credit-wallet-changed', this.onCreditWalletChanged)
    eventBus.off('credit-wallet-ai-changed', this._onCreditWalletAllowances)
    window.removeEventListener('notifications-updated', this.fetchNavbarCounts)
    window.removeEventListener('brand-theme-changed', this.onBrandThemeChanged)
    window.removeEventListener('resize', this.checkMobile)
    window.removeEventListener('scroll', this.handleScroll)
    document.removeEventListener('click', this.onDocumentClickSetup)
    document.body.style.overflow = ''
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');

.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--navbar-height, 86px);
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 31, 63, 0.1);
  box-shadow: 0 10px 30px rgba(0, 31, 63, 0.06);
  z-index: 999;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.navbar-scrolled {
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 14px 38px rgba(0, 31, 63, 0.1);
}

.navbar.public-navbar {
  --navbar-height: 72px;
  height: var(--navbar-height);
}

.navbar.public-navbar.public-navbar--tenant {
  --navbar-height: 50px;
  height: var(--navbar-height);
}

.public-navbar--tenant .navbar-brand :deep(.brand-logo-mark__img) {
  max-height: 32px;
}

.public-navbar .navbar-container {
  max-width: none;
  width: 100%;
  margin: 0;
  padding: 0 max(1.5rem, env(safe-area-inset-right, 0px)) 0 max(1.5rem, env(safe-area-inset-left, 0px));
  gap: 1.5rem;
}

.public-navbar .navbar-brand {
  background: transparent;
  box-shadow: none;
  padding: 0;
  flex-shrink: 0;
}

.public-navbar .navbar-brand:hover {
  box-shadow: none;
  transform: none;
}

.public-navbar .logo {
  height: 48px;
  width: auto;
  display: block;
}

.public-navbar .navbar-menu {
  flex: 1 1 auto;
  min-width: 0;
  justify-content: flex-end;
  margin-left: 0;
}

.public-navbar .mobile-menu-toggle {
  display: none;
}

@media (max-width: 1024px) {
  .public-navbar .mobile-menu-toggle {
    display: flex;
    margin-left: auto;
  }

  .public-navbar .navbar-container {
    gap: 0.75rem;
  }
}

/* Sidebar Integration */
.navbar.with-sidebar {
  --navbar-height: 64px;
  height: var(--navbar-height);
  left: var(--sidebar-width, 260px);
  right: 0;
  background: linear-gradient(
    90deg,
    var(--navbar-bg-start, #ffffff) 0%,
    var(--navbar-bg-mid, #f8fafc) 55%,
    var(--navbar-bg-end, #f1f5f9) 100%
  );
  color: var(--navbar-fg, var(--clinch-navy));
  border-bottom: 1px solid var(--navbar-border, rgba(var(--clinch-navy-rgb), 0.08));
  box-shadow: 4px 0 24px rgba(var(--clinch-navy-rgb), 0.04);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

.navbar.with-sidebar.navbar-scrolled {
  background: linear-gradient(
    90deg,
    var(--navbar-bg-start, #ffffff) 0%,
    var(--navbar-bg-mid, #f8fafc) 55%,
    var(--navbar-bg-end, #f1f5f9) 100%
  );
  box-shadow: 0 10px 28px rgba(var(--clinch-navy-rgb), 0.08);
}

.navbar.with-sidebar.navbar-layout-stack {
  border-left: 1px solid var(--navbar-border, rgba(var(--clinch-navy-rgb), 0.12));
  border-right: 1px solid var(--navbar-border, rgba(var(--clinch-navy-rgb), 0.12));
  box-sizing: border-box;
}

.navbar.with-sidebar.navbar-layout-cards,
.navbar.with-sidebar.navbar-layout-cards.sidebar-collapsed {
  left: var(--sidebar-collapsed-width, 80px);
}

.navbar.with-sidebar .icon-button,
.navbar.with-sidebar :deep(.help-launcher__button) {
  background: var(--navbar-hover-bg, rgba(255, 255, 255, 0.95));
  color: var(--navbar-fg, var(--clinch-navy));
  border-color: var(--navbar-border, rgba(var(--clinch-navy-rgb), 0.12));
  box-shadow: none;
}

.navbar.with-sidebar .icon-button:hover,
.navbar.with-sidebar :deep(.help-launcher__button:hover) {
  background: var(--navbar-active-bg, rgba(var(--clinch-primary-rgb), 0.14));
  color: var(--navbar-active-fg, var(--clinch-blue-primary));
  border-color: var(--navbar-border, rgba(var(--clinch-primary-rgb), 0.35));
  box-shadow: 0 8px 18px rgba(var(--clinch-primary-rgb), 0.12);
}

.navbar.with-sidebar .navbar-brand--dash-logo:hover {
  background: var(--navbar-hover-bg, rgba(var(--clinch-primary-rgb), 0.08));
}

.navbar.with-sidebar.sidebar-collapsed {
  left: var(--sidebar-collapsed-width, 80px);
}

.navbar-container {
  max-width: 1400px;
  margin: 0 auto;
  height: 100%;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
}

.container-with-sidebar {
  max-width: none;
  width: 100%;
  margin-left: 0;
  margin-right: 0;
  justify-content: flex-start;
  padding-left: 1rem;
  padding-right: 1.25rem;
  gap: 0;
}

/* Dashboard top strip: optional logo + sidebar toggle (sidebar width handled on .navbar) */
.navbar-dash-leading {
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  gap: 0.5rem;
}

.navbar-brand--dash-logo {
  padding: 0.2rem 0.45rem;
  margin-right: 0.15rem;
  box-shadow: none;
  background: transparent;
  border: none;
}

.navbar-brand--dash-logo:hover {
  transform: none;
  box-shadow: none;
  background: rgba(51, 181, 255, 0.08);
}

.navbar-brand--dash-logo .logo {
  height: 32px;
}

/* Pin headset + bell to the right edge of the top bar (both sidebar widths) */
.navbar-dash-actions {
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Icon top layout: profile sits on the right edge of the navbar */
.navbar-sidebar-profile {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.35rem;
  padding: 0.25rem 0.55rem 0.25rem 0.3rem;
  border-radius: 999px;
  background: var(--navbar-hover-bg, rgba(255, 255, 255, 0.92));
  border: 1px solid var(--navbar-border, rgba(var(--clinch-navy-rgb), 0.12));
  color: var(--navbar-fg, var(--clinch-navy));
  max-width: 11rem;
}

.navbar-sidebar-profile__avatar {
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: #fff;
  background: linear-gradient(
    135deg,
    var(--clinch-blue-primary) 0%,
    var(--clinch-blue-refresh-dark) 100%
  );
  box-shadow: 0 4px 12px rgba(var(--clinch-primary-rgb), 0.22);
}

.navbar-sidebar-profile__info {
  min-width: 0;
  line-height: 1.15;
}

.navbar-sidebar-profile__name {
  font-size: 0.75rem;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.navbar-sidebar-profile__role {
  font-size: 0.65rem;
  font-weight: 500;
  color: var(--navbar-fg-muted, rgba(var(--clinch-navy-rgb), 0.65));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Panel — match sidebar strip (rounded-square avatar) when Icon top */
.navbar-sidebar-profile--panel {
  border-radius: 0.65rem;
  padding: 0.3rem 0.5rem 0.3rem 0.3rem;
  background: var(--navbar-hover-bg, rgba(var(--clinch-primary-rgb), 0.08));
}

.navbar-sidebar-profile--panel .navbar-sidebar-profile__avatar {
  border-radius: 0.45rem;
}

/* Pill — already the default capsule; keep circular avatar */
.navbar-sidebar-profile--pill {
  border-radius: 999px;
}

.navbar-sidebar-profile--pill .navbar-sidebar-profile__avatar {
  border-radius: 999px;
  box-shadow: none;
}

/* Card */
.navbar-sidebar-profile--card {
  border-radius: 0.75rem;
  padding: 0.35rem 0.55rem 0.35rem 0.35rem;
  background: var(--navbar-hover-bg, rgba(255, 255, 255, 0.95));
}

.navbar-sidebar-profile--card .navbar-sidebar-profile__avatar {
  border-radius: 0.5rem;
}

/* Minimal */
.navbar-sidebar-profile--minimal {
  border: 0;
  background: transparent;
  padding: 0.15rem 0.25rem;
}

.navbar-sidebar-profile--minimal .navbar-sidebar-profile__avatar {
  border-radius: 999px;
  box-shadow: none;
}

@media (max-width: 640px) {
  .navbar-sidebar-profile__info {
    display: none;
  }

  .navbar-sidebar-profile {
    padding: 0.2rem;
    max-width: none;
  }
}

/* Logo */
.navbar-brand {
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: all 0.3s ease;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.navbar-brand:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.logo {
  height: 40px;
  width: auto;
  transition: all 0.3s ease;
}

/* Dashboard-only: outline hamburger (marketing / public keeps gradient via .public-navbar if needed) */
.navbar.with-sidebar .sidebar-toggle:not(.mobile-toggle) {
  background: var(--navbar-hover-bg, #fff);
  color: var(--navbar-fg-muted, #64748b);
  box-shadow: none;
  border: 1px solid var(--navbar-border, rgba(var(--clinch-navy-rgb), 0.12));
  width: 40px;
  height: 40px;
  margin-right: 0;
  border-radius: 10px;
}

.navbar.with-sidebar .sidebar-toggle:not(.mobile-toggle):hover {
  background: var(--navbar-active-bg, rgba(var(--clinch-primary-rgb), 0.1));
  color: var(--navbar-active-fg, var(--clinch-blue-primary));
  transform: none;
  box-shadow: 0 2px 8px rgba(var(--clinch-navy-rgb), 0.06);
}

.navbar.with-sidebar .sidebar-toggle:not(.mobile-toggle):active {
  transform: none;
}

.navbar-brand:hover .logo {
  transform: scale(1.02);
}

/* Sidebar toggle — default (mobile rail / fallbacks): strong Clinch CTA */
.sidebar-toggle {
  width: 44px;
  height: 44px;
  border: none;
  background: linear-gradient(135deg, var(--clinch-blue-primary) 0%, var(--clinch-blue-refresh-dark) 100%);
  color: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  margin-right: 1rem;
  flex-shrink: 0;
  box-shadow: 0 8px 22px rgba(51, 181, 255, 0.28);
}

.sidebar-toggle:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(51, 181, 255, 0.35);
}

.sidebar-toggle:active {
  transform: translateY(0);
}

/* When sidebar is collapsed to rail, sidebar expand is on the rail — hide duplicate desktop hamburger */
.navbar.sidebar-collapsed .sidebar-toggle:not(.mobile-toggle):not(.nav-apps-toggle) {
  display: none;
}

/* Mobile styles - applied only on mobile */
@media (max-width: 768px) {
  .navbar.with-sidebar,
  .navbar.with-sidebar.sidebar-collapsed {
    left: 0 !important;
    right: 0;
  }
  
  .navbar-container {
    padding: 0 1rem;
  }

  .navbar-dash-leading {
    flex: 1 1 auto;
    min-width: 0;
  }

  .navbar-dash-actions,
  .navbar-user-menu.navbar-dash-actions {
    order: 2;
    flex: 0 0 auto;
    margin-left: auto !important;
    margin-right: 0 !important;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .navbar.with-sidebar .sidebar-toggle.mobile-toggle {
    margin-left: 0;
    margin-right: 0;
    background: var(--navbar-hover-bg, #fff);
    color: var(--navbar-fg-muted, #64748b);
    box-shadow: none;
    border: 1px solid var(--navbar-border, rgba(var(--clinch-navy-rgb), 0.12));
    width: 40px;
    height: 40px;
    border-radius: 10px;
  }

  .navbar.with-sidebar .sidebar-toggle.mobile-toggle:hover {
    transform: none;
    background: var(--navbar-active-bg, rgba(var(--clinch-primary-rgb), 0.1));
    color: var(--navbar-active-fg, var(--clinch-blue-primary));
    box-shadow: 0 2px 8px rgba(var(--clinch-navy-rgb), 0.06);
  }
  
  .logo {
    height: 36px;
    margin: 0 !important;
  }
  
  .container-with-sidebar {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
  }
  
  .navbar .container {
    margin-left: 0 !important;
    margin-right: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
  }
  
  .sidebar-toggle {
    margin-right: 0 !important;
  }
  
  .sidebar-toggle.mobile-toggle {
    margin-left: 0.5rem !important;
  }
}

.navbar-toggler {
  border-color: #003366;
}

.navbar-toggler-icon {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba%280, 51, 102, 1%29' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e");
}

.navbar-nav .nav-link {
  color: var(--clinch-navy) !important;
  font-weight: 500;
  margin: 0 0.5rem;
  transition: color 0.3s ease;
}

.navbar-nav .nav-link:hover {
  color: var(--clinch-blue-primary) !important;
}

.nav-link-subscribe {
  background: linear-gradient(135deg, #0066CC 0%, #003366 100%);
  color: white !important;
  border-radius: 5px;
  padding: 0.5rem 1rem !important;
  position: relative;
}

.nav-link-subscribe:hover {
  background: linear-gradient(135deg, #0052a3 0%, #00264d 100%);
  color: white !important;
}

/* Mobile Menu Toggle */
.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 32px;
  height: 26px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1001;
}

.mobile-menu-toggle span {
  width: 32px;
  height: 3px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
}

.public-navbar .mobile-menu-toggle span {
  background: var(--clinch-navy);
}

.mobile-menu-toggle.active span:nth-child(1) {
  transform: translateY(11px) rotate(45deg);
}

.mobile-menu-toggle.active span:nth-child(2) {
  opacity: 0;
  transform: translateX(20px);
}

.mobile-menu-toggle.active span:nth-child(3) {
  transform: translateY(-12px) rotate(-45deg);
}

/* User Menu Styles - Simplified */
.navbar-user-menu {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
  flex-shrink: 0;
}

.icon-button,
:deep(.help-launcher__button) {
  width: 44px;
  height: 44px;
  padding: 0;
  background: var(--btn-nav-icon, rgba(255, 255, 255, 0.95));
  color: var(--btn-nav-icon-text, var(--clinch-navy));
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.1rem;
  line-height: 1;
  position: relative;
  border: 1px solid var(--btn-nav-icon-border, rgba(0, 31, 63, 0.12));
  box-shadow: 0 2px 10px rgba(var(--btn-nav-icon-rgb, 0, 31, 63), 0.06);
}

.icon-button:hover,
:deep(.help-launcher__button:hover) {
  background: var(--btn-nav-icon-hover, rgba(51, 181, 255, 0.12));
  color: var(--btn-nav-icon-hover-text, var(--clinch-blue-primary));
  border-color: rgba(var(--btn-nav-icon-hover-rgb, 51, 181, 255), 0.35);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(var(--btn-nav-icon-hover-rgb, 51, 181, 255), 0.15);
}

.icon-button:active,
:deep(.help-launcher__button:active) {
  transform: translateY(0);
}

.credit-wallet-nav-button,
.orders-nav-button,
.tickets-nav-button,
.support-tickets-button,
.setup-workspace-button,
:deep(.help-launcher__button) {
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.credit-wallet-nav-button--balance {
  width: auto;
  padding: 0 0.75rem;
  gap: 0.45rem;
}

.credit-wallet-nav-amount {
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.credit-wallet-nav-button--empty {
  color: #b45309;
  border-color: rgba(180, 83, 9, 0.45);
  animation: pulse 2s infinite;
}

.credit-wallet-nav-amount--empty {
  color: #b45309;
}

.credit-wallet-nav-warn {
  font-size: 0.75rem;
}

.credit-wallet-nav-meter {
  font-size: 0.7rem;
  font-weight: 600;
  color: inherit;
  padding-left: 0.4rem;
  margin-left: 0.15rem;
  border-left: 1px solid color-mix(in srgb, currentColor 35%, transparent);
}

.credit-wallet-nav-meter.is-charging {
  color: #b45309;
}

@media (max-width: 640px) {
  .credit-wallet-nav-button--balance {
    width: auto;
    max-width: 100%;
    padding: 0 0.45rem;
  }

  .credit-wallet-nav-amount {
    display: inline-flex;
    font-size: 0.68rem;
  }
}

.setup-workspace-button--alert {
  color: #b45309;
  border-color: rgba(180, 83, 9, 0.35);
  animation: pulse 2s infinite;
}

.setup-nav {
  position: relative;
}

.setup-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  z-index: 1200;
  width: min(22rem, calc(100vw - 1.5rem));
  padding: 0.75rem 0.65rem 0.5rem;
  border-radius: 0.85rem;
  background: #fff;
  border: 1px solid rgba(var(--clinch-navy-rgb, 0, 31, 63), 0.12);
  box-shadow: 0 16px 40px rgba(0, 31, 63, 0.16);
  color: var(--clinch-navy, #001f3f);
}

.setup-dropdown__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.15rem 0.45rem 0.65rem;
  border-bottom: 1px solid rgba(var(--clinch-navy-rgb, 0, 31, 63), 0.08);
  margin-bottom: 0.35rem;
}

.setup-dropdown__head strong {
  font-size: 0.9rem;
}

.setup-dropdown__count {
  font-size: 0.72rem;
  font-weight: 600;
  color: #b45309;
}

.setup-dropdown__count--ok {
  color: #047857;
}

.setup-dropdown__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: min(22rem, 55vh);
  overflow: auto;
}

.setup-dropdown__item {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  column-gap: 0.5rem;
  row-gap: 0.15rem;
  align-items: start;
  padding: 0.65rem 0.55rem;
  border-radius: 0.65rem;
  text-decoration: none;
  color: inherit;
  transition: background 0.15s ease;
}

.setup-dropdown__item:hover {
  background: rgba(var(--clinch-primary-rgb, 51, 181, 255), 0.1);
}

.setup-dropdown__item-label {
  grid-column: 1;
  grid-row: 1;
  font-size: 0.86rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.setup-dropdown__item-detail {
  grid-column: 1;
  grid-row: 2;
  font-size: 0.72rem;
  line-height: 1.35;
  color: rgba(var(--clinch-navy-rgb, 0, 31, 63), 0.65);
}

.setup-dropdown__chevron {
  grid-column: 2;
  grid-row: 1 / span 2;
  align-self: center;
  font-size: 0.7rem;
  color: rgba(var(--clinch-navy-rgb, 0, 31, 63), 0.4);
}

.setup-dropdown__pill {
  display: inline-flex;
  align-items: center;
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #92400e;
  background: rgba(245, 158, 11, 0.18);
}

.setup-dropdown__empty {
  margin: 0.35rem 0.45rem 0.75rem;
  font-size: 0.8rem;
  color: rgba(var(--clinch-navy-rgb, 0, 31, 63), 0.65);
}

.setup-dropdown__footer {
  display: block;
  margin-top: 0.25rem;
  padding: 0.55rem 0.55rem 0.35rem;
  border-top: 1px solid rgba(var(--clinch-navy-rgb, 0, 31, 63), 0.08);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--clinch-blue-primary, #0081c9);
  text-decoration: none;
}

.setup-dropdown__footer:hover {
  text-decoration: underline;
}

.notification-button.has-notifications {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.notification-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  background: var(
    --btn-nav-icon-badge-gradient,
    linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%)
  );
  color: white;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.notification-badge--setup {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

/* Navigation Menu ( :deep — links live in PublicNavLinks.vue ) */
.navbar-menu {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.navbar-menu :deep(.public-nav-links) {
  flex-wrap: nowrap;
}

@media (max-width: 1024px) {
  .public-navbar .mobile-menu-toggle {
    display: flex;
    margin-left: auto;
  }

  /* Public marketing drawer from 1024px down */
  .public-navbar .navbar-menu {
    position: fixed;
    top: var(--navbar-height, 72px);
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    margin-left: 0;
    background: #ffffff;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    padding: 1.25rem 1.25rem max(1.25rem, env(safe-area-inset-bottom));
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .public-navbar .navbar-menu.show {
    transform: translateX(0);
  }
}

@media (max-width: 768px) {
  .mobile-menu-toggle {
    display: flex;
  }
  
  /* Mobile Menu Overlay — light panel: use dark text on links (was white-on-white). */
  .navbar-menu {
    position: fixed;
    top: var(--navbar-height, 86px);
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    margin-left: 0;
    background: #ffffff;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    padding: 1.25rem 1.25rem max(1.25rem, env(safe-area-inset-bottom));
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  .navbar-menu.show {
    transform: translateX(0);
  }

  .public-navbar .navbar-menu {
    top: var(--navbar-height, 72px);
  }
  
  .navbar-menu :deep(.nav-links) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    width: 100%;
    flex-shrink: 0;
  }

  .navbar-menu :deep(.nav-link:not(.nav-link-cta)) {
    color: #1a202c;
    background: transparent;
    border: 1px solid rgba(26, 32, 44, 0.08);
  }

  .navbar-menu :deep(.nav-link:not(.nav-link-cta):hover) {
    color: #0066cc;
    background: rgba(0, 102, 204, 0.06);
  }

  .navbar-menu :deep(.nav-link) {
    width: 100%;
    padding: 1rem 1.25rem;
    font-size: 1.05rem;
    justify-content: space-between;
  }

  .navbar-menu :deep(.nav-link-cta) {
    margin-top: 0.5rem;
    border: none;
  }
  
  .icon-button,
  :deep(.help-launcher__button) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .logo {
    height: 34px;
  }

  .public-navbar .logo {
    height: 52px;
  }

  .public-navbar.public-navbar--tenant .logo {
    height: 32px;
  }
  
  .navbar {
    height: 76px;
  }

  .navbar.public-navbar {
    height: 76px;
  }

  .navbar.public-navbar.public-navbar--tenant {
    height: var(--navbar-height, 50px);
  }
  
  .navbar-menu {
    top: 76px;
  }

  .public-navbar .navbar-menu {
    top: 76px;
  }
  
  .icon-button,
  :deep(.help-launcher__button) {
    width: 36px;
    height: 36px;
    font-size: 0.95rem;
  }
}

/* Desktop styles - applied from 769px onwards */
@media (min-width: 769px) {
  .navbar.with-sidebar {
    left: var(--sidebar-width, 260px);
  }
  
  .navbar.with-sidebar.sidebar-collapsed {
    left: var(--sidebar-collapsed-width, 80px);
  }

  .navbar.with-sidebar.navbar-layout-cards,
  .navbar.with-sidebar.navbar-layout-cards.sidebar-collapsed {
    left: var(--sidebar-collapsed-width, 80px);
  }
  
  .navbar.with-sidebar .navbar-container.container-with-sidebar {
    flex-wrap: nowrap;
    width: 100%;
    max-width: none;
  }
}

/* Tablet adjustments */
@media (min-width: 769px) and (max-width: 991px) {
  .logo {
    height: 42px;
  }
}
</style>